// Copyright © 2023 Move Closer
import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { loadScript } from '../../../support';
import { AbstractAdBanner } from './AbstractAdBanner.ui';
import { tolerances } from '../AdBanner.config';
// Global flag to prevent duplicate event listeners
/**
 * Component capable of rendering the `AdBannerModule's` contents.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
let AdBannerGoogle = class AdBannerGoogle extends AbstractAdBanner {
    constructor() {
        super(...arguments);
        this.slot = null;
        this.slotRenderHandler = null;
    }
    get hasValidConfig() {
        const isValid = !!this.module.content && !!this.module.content.networkCode && !!this.module.content.adUnit;
        if (!isValid) {
            if (typeof this.onEmptyCallback === 'undefined') {
                this.onEmpty();
            }
            else {
                this.onEmptyCallback();
            }
        }
        return isValid;
    }
    /**
     * Initialises the ad banner.
     */
    initAd() {
        if (!this.hasValidConfig) {
            console.error('bad config for ad banner');
            return;
        }
        try {
            const networkCode = this.module.content.networkCode;
            if (typeof networkCode === 'undefined') {
                this.onError(new Error(`AdBannerModuleUi.initAd(): Failed to resolve the [networkCode]!\n[this.module.content.networkCode] = [${this.module.content.networkCode}]`));
                return;
            }
            const adUnit = this.module.content.adUnit;
            const adUnitFullPath = this.module.content.adUnitFullPath;
            if (typeof adUnit === 'undefined') {
                this.onError(new Error(`AdBannerModuleUi.initAd(): Failed to resolve the [adUnit]!\n[this.module.content.adUnit] = [${this.module.content.adUnit}]`));
                return;
            }
            const creativesDimensions = this.creativesDimensions;
            if (typeof creativesDimensions === 'undefined' ||
                !Array.isArray(creativesDimensions) ||
                creativesDimensions.length === 0) {
                this.onError(new Error('AdBannerModuleUi.initAd(): Failed to resolve the applicable [creativesDimensions]!'));
                return;
            }
            let elementId = this.elementId;
            if (typeof elementId !== 'string' || elementId.length === 0) {
                this.setElementId();
                elementId = this.elementId;
            }
            const finalAdUnit = adUnitFullPath || adUnit;
            this.defineGoogleAds(networkCode, finalAdUnit, creativesDimensions, elementId);
            this.onReady();
        }
        catch (error) {
            this.onError(error);
        }
    }
    /**
     * Additional ad's targeting.
     */
    defineGoogleTargeting() {
        const urlPathName = document.location.pathname;
        if (typeof this.module.content.targeting === 'object' &&
            this.module.content.targeting !== null &&
            Object.keys(this.module.content.targeting).length > 0) {
            Object.entries(this.module.content.targeting)
                .forEach(([key, _values]) => {
                /**
                 * The `values` argument for the `setTargeting()` function call.
                 */
                const values = Array.isArray(_values) ? _values : [_values];
                if (this.slot) {
                    this.slot.setTargeting(key, values);
                }
            });
            if (this.slot) {
                const urlPathParts = urlPathName.split('/');
                if (urlPathName && urlPathParts.length) {
                    this.slot.setTargeting('url', urlPathName.slice(-40));
                    this.slot.setTargeting('category', urlPathParts[1].slice(-40));
                }
            }
        }
    }
    defineGoogleAds(networkCode, adUnit, creativesDimensions, elementId) {
        googletag.cmd.push(() => {
            var _a, _b;
            this.slot = (_b = (_a = googletag.defineSlot(`/${networkCode}/${adUnit}`, creativesDimensions, elementId)) === null || _a === void 0 ? void 0 : _a.addService(googletag.pubads())) !== null && _b !== void 0 ? _b : null;
            this.defineGoogleTargeting();
            // Add event listener only once per application instance
            // Add instance-specific event listener only if not already added
            if (!this.slotRenderHandler) {
                this.slotRenderHandler = (event) => {
                    if (this.slot === event.slot) {
                        if (window.localStorage && window.localStorage.getItem('adsDebug') === '1') {
                            console.info('Ad', {
                                sizes: event.slot.getSizes(),
                                getName: event.slot.getSlotId().getName(),
                                getId: event.slot.getSlotId().getId(),
                                getDomId: event.slot.getSlotId().getDomId(),
                                adUnitPathId: event.slot.getSlotId().getAdUnitPath(),
                                adUnitPath: event.slot.getAdUnitPath(),
                                size: event.size,
                                campaignId: event.campaignId,
                                serviceName: event.serviceName
                            });
                        }
                        if (event.isEmpty && this.shouldShrink) {
                            return;
                        }
                        // this.checkSizes(event.size)
                        this.$nextTick(() => {
                            this.isEmpty = false;
                        });
                    }
                };
                googletag.pubads().addEventListener('slotRenderEnded', this.slotRenderHandler);
            }
            googletag.pubads().enableSingleRequest();
            // googletag.pubads().collapseEmptyDivs(this.shouldShrink)
            googletag.enableServices();
        });
        googletag.cmd.push(function () {
            googletag.display(elementId);
        });
        googletag.cmd.push(() => {
            if (this.slot !== null) {
                googletag.pubads().refresh([this.slot]);
            }
        });
    }
    checkSizes(size) {
        if (!Array.isArray(size) || size.length < 2 || !this.bannerContainer) {
            return;
        }
        const resolvedAdRatio = AbstractAdBanner.roundRatio(size[1], size[0]);
        const containerRatio = AbstractAdBanner.roundRatio(this.bannerContainer.clientHeight, this.bannerContainer.clientWidth);
        const paddingTolerance = tolerances.padding;
        const widthTolerance = tolerances.width;
        const ratioDifference = resolvedAdRatio - containerRatio;
        const widthDifference = this.bannerContainer.clientWidth - size[0];
        if (widthDifference > widthTolerance || widthDifference < -widthTolerance) {
            this.changeSectionStyl(size[0] + 1);
        }
        if (ratioDifference > paddingTolerance || ratioDifference < -paddingTolerance) {
            this.changeContainerStyle(resolvedAdRatio * 100);
        }
    }
};
AdBannerGoogle = __decorate([
    Component({
        name: 'AdBannerGoogle',
        mounted() {
            const source = (typeof process.env.VUE_APP_GPT_SCRIPT_SRC === 'string' &&
                process.env.VUE_APP_GPT_SCRIPT_SRC.length > 0)
                ? process.env.VUE_APP_GPT_SCRIPT_SRC
                : 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
            loadScript(source).then(() => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                // @ts-ignore
                if (typeof window.googletag !== 'object' || !window.googletag) {
                    console.warn('[AdBanner] GoogleTag is not a valid service.');
                    return;
                }
                window.googletag = window.googletag || { cmd: [] };
                this.initAd();
            }).catch(() => {
                console.warn('[AdBanner] Failed to load GoogleTag scripts.');
            });
        },
        beforeDestroy() {
            if (this.slot) {
                googletag.destroySlots([this.slot]);
            }
            // Remove event listener if it was added
            if (this.slotRenderHandler) {
                googletag.pubads().removeEventListener('slotRenderEnded', this.slotRenderHandler);
                this.slotRenderHandler = null;
            }
        }
    })
], AdBannerGoogle);
export { AdBannerGoogle };
